import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import styles from "@/components/PlatformSection.module.css";
import DataProductDemoEmbed from "@/components/platform-section/DataProductDemoEmbed";
import ScoutDemoEmbed from "@/components/platform-section/ScoutDemoEmbed";
import WorkflowsDemoEmbed from "@/components/platform-section/WorkflowsDemoEmbed";

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

interface Stage {
  id: string;
  label: string;
  title: string;
  description: string;
}

const STAGES: Stage[] = [
  {
    id: "scout",
    label: "Scout",
    title: "Your team defines the job in plain language.",
    description:
      "Tell Scout what to check, what to extract, and what should trigger follow-up. It turns that instruction into a repeatable process your operators can run without forms, handoffs, or technical setup.",
  },
  {
    id: "workflows",
    label: "Workflows",
    title: "Execution keeps running on every new document.",
    description:
      "Workflows operationalize the instruction: matching invoices to approvals, tracking vendor renewals, and flagging exceptions the moment they appear. Every run feeds the outcome back into the workspace so the next decision starts with current evidence.",
  },
  {
    id: "workspace",
    label: "Workspace",
    title: "Documents become a maintained operating record.",
    description:
      "Invoices, contracts, and supporting files land in one workspace where each result stays queryable, reusable, and tied back to its source. When a new document arrives, Lumient updates the same record instead of making your team reconstruct the answer from scratch.",
  },
];

// ---------------------------------------------------------------------------
// Animation variants — entrance only
// ---------------------------------------------------------------------------

const EASE = [0.25, 0.1, 0.25, 1] as const;

const headerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const headerItemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function progressToStage(progress: number): number {
  if (progress < 1 / 3) return 0;
  if (progress < 2 / 3) return 1;
  return 2;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface TextPanelProps {
  stage: Stage;
  active: boolean;
}

const TextPanel = ({ stage, active }: TextPanelProps) => (
  <div
    className={`${styles.textPanel} ${active ? styles.textPanelActive : styles.textPanelInactive}`}
    aria-hidden={!active}
  >
    <p className={styles.eyebrow}>{stage.label}</p>
    <h3 className={styles.panelTitle}>{stage.title}</h3>
    <p className={styles.panelDescription}>{stage.description}</p>
  </div>
);

interface MediaPanelProps {
  stageId: string;
  active: boolean;
}

const MediaPanel = ({ stageId, active }: MediaPanelProps) => (
  <div
    className={`${styles.mediaPanel} ${active ? styles.mediaPanelActive : styles.mediaPanelInactive}`}
    aria-hidden={!active}
  >
    <div className={styles.mediaPanelInner}>
      {stageId === "workspace" && <DataProductDemoEmbed active={active} />}
      {stageId === "scout" && <ScoutDemoEmbed active={active} />}
      {stageId === "workflows" && <WorkflowsDemoEmbed active={active} />}
    </div>
  </div>
);

const TRACK_LABELS = ["01", "02", "03"] as const;
const TRACK_POSITIONS = [0, 50, 100] as const;

interface ProgressTrackProps {
  activeIndex: number;
  scrollProgress: number;
}

const ProgressTrack = ({ activeIndex, scrollProgress }: ProgressTrackProps) => {
  const dotPositionPercent = clamp(scrollProgress * 100, 0, 100);

  return (
    <div className={styles.progressTrack} aria-hidden="true">
      <div className={styles.trackLine}>
        <div
          className={styles.trackTrail}
          style={{ height: `${dotPositionPercent}%` }}
        />
        <div
          className={styles.trackDot}
          style={{ top: `${dotPositionPercent}%` }}
        />
      </div>
      <div className={styles.trackLabels}>
        {TRACK_LABELS.map((label, i) => (
          <span
            key={label}
            className={`${styles.trackNumber} ${i === activeIndex ? styles.trackNumberActive : ""}`}
            style={{ top: `${TRACK_POSITIONS[i]}%` }}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Desktop sticky slider
// ---------------------------------------------------------------------------

const StickySlider = () => {
  const outerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const el = outerRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const totalScrollable = rect.height - window.innerHeight;
        if (totalScrollable <= 0) {
          setScrollProgress(0);
          return;
        }
        const scrolled = clamp(-rect.top, 0, totalScrollable);
        setScrollProgress(scrolled / totalScrollable);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const activeIndex = progressToStage(scrollProgress);

  return (
    <div ref={outerRef} className={styles.sliderOuter}>
      <div className={styles.sliderSticky}>
        <div className={styles.sliderGrid}>
          {/* Left: text panels */}
          <div className={styles.textColumn}>
            {STAGES.map((stage, i) => (
              <TextPanel key={stage.id} stage={stage} active={i === activeIndex} />
            ))}
          </div>

          {/* Center: media embeds — vertically centered */}
          <div className={styles.mediaColumn}>
            {STAGES.map((stage, i) => (
              <MediaPanel key={stage.id} stageId={stage.id} active={i === activeIndex} />
            ))}
          </div>

          {/* Right: progress track — independent column */}
          <div className={styles.trackColumn}>
            <ProgressTrack activeIndex={activeIndex} scrollProgress={scrollProgress} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mobile stacked layout
// ---------------------------------------------------------------------------

const MobileStack = () => (
  <div className={styles.mobileStack}>
    {STAGES.map((stage) => (
      <div key={stage.id} className={styles.mobileStage}>
        <p className={styles.eyebrow}>{stage.label}</p>
        <h3 className={styles.panelTitle}>{stage.title}</h3>
        <p className={styles.panelDescription}>{stage.description}</p>
        <div className={styles.mobileMedia}>
          {stage.id === "workspace" && <DataProductDemoEmbed active />}
          {stage.id === "scout" && <ScoutDemoEmbed active />}
          {stage.id === "workflows" && <WorkflowsDemoEmbed active />}
        </div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

const PlatformSection = () => {
  return (
    <section id="platform" className={`${styles.section} px-6 lg:px-16`}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          className={styles.header}
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
        >
          <motion.h2 variants={headerItemVariants} className={styles.heading}>
            The Lumient Platform
          </motion.h2>
          <motion.p variants={headerItemVariants} className={styles.subheading}>
            Three components. One operational layer.
          </motion.p>
          <motion.p variants={headerItemVariants} className={styles.intro}>
            Lumient works as one connected ecosystem: documents are captured into a maintained workspace, Scout turns an
            operating question into a repeatable process, and workflows keep that process running every time new evidence
            arrives.
          </motion.p>
        </motion.div>
      </div>

      <div className={`max-w-7xl mx-auto ${styles.desktopOnly}`}>
        <StickySlider />
      </div>

      <div className={`max-w-7xl mx-auto ${styles.mobileOnly}`}>
        <MobileStack />
      </div>
    </section>
  );
};

export default PlatformSection;
